import { Component, useCallback, useContext, useEffect, useState } from "react";
import { Edge, Handle, Node, NodeProps, NodeToolbar, Position, useReactFlow, useStore, useUpdateNodeInternals, XYPosition } from "reactflow";
import { ReactComponent as DragHandle } from '../../../assets/drag-handle.svg';
import ExpandToolbar from "../../components/expand-toolbar/expand-toolbar";
import { ResponseState } from "../../components/input.model";
import useDetachNodes from '../../hooks/useDetachNodes';
import { InstanceState } from "../../triggers/semantic-dive/semantic-dive";
import { createBrainstormNode, createChatNode } from "../response-node/response-node.helper";
import { ChatNodeData, TypeChatNode } from "../response-node/response-node.model";
import { InputHoverState, ZoomState } from "../node.model";
import { TypeTopicNode } from "./topic-node.model";
import './topic-node.scss';
import { getGPT3Questions } from "../../../api/openai-api"
import TextArea from "antd/es/input/TextArea";
import { ReactComponent as EditIcon1 } from '../../../assets/node-icons/edit-1.svg';
import { ReactComponent as EditIcon2 } from '../../../assets/node-icons/edit-2.svg';
import { zoomLimits } from "../../utils";
import { BrainstormNodeData } from "../brainstorm-node/brainstorm-node.model";
import { extractNodeTexts, predictSubtopics } from "../node.helper";
import { InstanceMapContext } from "../../pages/canvas-view/canvas-flow";
import { getAllReferenceCopies } from "./topic-node.helper";

const zoomSelector = (s: any) => s.transform[2];

const TopicNode = (props: NodeProps) => {
  const {instanceMap, setInstanceMap} = useContext(InstanceMapContext);
  const reactFlowInstance = useReactFlow();

  const [topic, setTopic] = useState(props.data.state.topic ?? '');
  if (instanceMap[props.id] && instanceMap[props.id].topicNode.data.state.topic !== topic) {
    setTopic(instanceMap[props.id].topicNode.data.state.topic);
  }
  // console.log('->', topic, instanceMap[props.id]?.topicNode.data.state.topic);
  const [toolbarAvailable, setToolbarAvailable] = useState(props.data.state.toolbarAvailable ?? true);
  const [toolbarViewState, setToolbarViewState] = useState(props.data.state.toolbarViewState ?? InputHoverState.OUT);
  const [isRecommended, setIsRecommended] = useState(props.data.state.isRecommended ?? false);
  const [isEditing, setIsEditing] = useState(false);

  const [hasTopic, setHasTopic] = useState(props.data.state.hasTopic ?? true);
  const [addingRecommendation, setAddingRecommendation] = useState(false);
  const [canEdit, setCanEdit] = useState(props.data.state.canEdit ?? true);
  const zoom: number = useStore(zoomSelector);

  const updateNodeInternals = useUpdateNodeInternals();

  // parent node is the group node
  const isInGroup = useStore((store) => !!store.nodeInternals.get(props.id)?.parentNode);
  const detachNodes = useDetachNodes();

  const onDelete = () => reactFlowInstance.deleteElements({ nodes: [{ id: props.id }] });
  const onDetach = () => detachNodes([props.id]);

  useEffect(() => {
    reactFlowInstance.setNodes(nodes => nodes.map(node => {
      if (node.id === props.id) {
        node.data.state = {
          topic,
          toolbarAvailable,
          toolbarViewState,
          isRecommended,
        }
      }
      return node;
    }))
  }, [reactFlowInstance, topic, toolbarAvailable, toolbarViewState, isRecommended]);

  // Make chat response
  const askTopicAsQuestion = useCallback(
    () => {
      const data: ChatNodeData = {
        chatHistory: props.data.chatHistory ?? [],
        // We want chat node to already show a response
        placeholder: '',
        state: {
          input: topic,
          responseInputState: ResponseState.LOADING
        }
      };
      createChatNode(reactFlowInstance, props.id, data);
    },
    [reactFlowInstance, topic, props]
  );

  const getInstanceStateClassName = (instanceState: InstanceState) => {
    if (instanceState === InstanceState.CURRENT) {
      return 'current-semantic-instance';
    } else if (instanceState === InstanceState.WAS) {
      return 'was-semantic-instance';
    }
  }

  const handleChange = (event: any) => {
    setTopic(event.target.value);
  }

  const endEditing = useCallback(() => {
    if (topic.trim()) {
      document.getElementById('hierarchy-topic-textarea')?.blur();
      updateTopicName(props.id, topic.trim());
      setIsEditing(false);
      setHasTopic(true);
    }
  }, [isEditing, topic]);

  /**
   * Update the name of the topic across all topic nodes and its copies
   * Reference function: layer.tsx
   */
  const updateTopicName = useCallback((topicId: string, topicName: string) => {
    if (instanceMap[topicId]) {
      const references = getAllReferenceCopies(instanceMap[topicId].topicNode, instanceMap);

      // if it's a parent node of the child, 
      references.forEach(topicNode => {
        topicNode.data.state.topic = topicName;
      });

      instanceMap[topicId].topicNode.data.state.topic = topicName;
      setInstanceMap(instanceMap);
    }
  }, [reactFlowInstance, instanceMap]);

  const handleEnterPress = (event: any) => {
    if (event.key === 'Enter' && event.shiftKey) {
      endEditing();
      askTopicAsQuestion();
    } else if (event.key === 'Enter'){
      endEditing();
    }
  }

  // move viewport to node when input on focus
  const handleOnFocus = useCallback(
    (event: any) => {
      // props.setInputState(InputHoverState.CLICKED);
      const sourceNode = reactFlowInstance.getNode(props.id);
      if (sourceNode) {
        reactFlowInstance.fitView({
          duration: 900,
          padding: 1,
          maxZoom: zoomLimits.max,
          minZoom: zoomLimits.min,
          nodes: [sourceNode],
        });
      }
    },
    [reactFlowInstance]
  );

  return (
    <div
      id={props.id}
      className={`topic-node ${zoom >= ZoomState.ALL ? '' : 'drag-handle'}
        ${isRecommended ? 'recommended-topic' : ''}
        ${addingRecommendation ? 'adding-recommendation' : ''}`}
      onClick={() => {
        console.log(topic, reactFlowInstance.getNode(props.id))
        if (isRecommended) {
          setIsRecommended(false);
          setAddingRecommendation(true);
        } else if (toolbarViewState !== InputHoverState.HOVER) {
          setToolbarViewState(InputHoverState.HOVER);
        }
      }}
      // onMouseEnter={() => toolbarViewState === InputHoverState.OUT && setToolbarViewState(InputHoverState.HOVER)}
      onMouseEnter={() => {
        // if (!isRecommended) {
        //   setToolbarViewState(InputHoverState.HOVER);
        // } 
      }}
      onMouseLeave={() => (toolbarViewState === InputHoverState.HOVER  && !isRecommended) && setToolbarViewState(InputHoverState.OUT)}
      // style={{
      //   transform: `scale(${Math.max(1/(zoom*1.3), 1)}) translate(${zoom <= 1/1.3 ? '-100px' : '0'})`
      // }}
    >
      {
        isInGroup &&
        <NodeToolbar className="nodrag">
          <button className="detach-button" onClick={onDetach}>Detach</button>
        </NodeToolbar>
      }
      <div
        className={`node topic-node-box ${getInstanceStateClassName(props.data.instanceState)} `}>
        <DragHandle className='drag-handle' />
        { isEditing || !hasTopic ? 
            <TextArea
              id='hierarchy-topic-textarea'
              className='text-input'
              value={topic}
              onChange={handleChange}
              onFocus={handleOnFocus}
              onBlur={endEditing}
              style={{
                border: 'none',
                outline: 'none',
                boxShadow: 'none',
              }}
              autoSize
              onKeyDown={handleEnterPress}
            /> 
          : 
          <div className='topic-name'>
            {topic} { canEdit? <div className='edit-topic' onClick={() => (setIsEditing(true))}><EditIcon2/></div>: <></>}
          </div>
        }
        <span className="dive-in-icon"></span>
        <span className="dive-out-icon"></span>
      </div>
      {toolbarAvailable && !isEditing && hasTopic && (zoom > ZoomState.SUMMARY) ?
        <NodeToolbar isVisible={toolbarViewState !== InputHoverState.OUT} position={Position.Bottom} style={{zIndex: 10000}}>
          <ExpandToolbar
            sourceId={props.id}
            setInputState={setToolbarViewState}
            setToolbarAvailable={setToolbarAvailable}
            topic={topic}
          />
        </NodeToolbar>
        : <></>
      }
      <Handle id="target-e-1" type="target" position={Position.Top} className="node-handle-direct visible target-handle"/>
      <Handle id="source-e-1" type="source" position={Position.Bottom} className="node-handle-direct visible source-handle"/>
      <Handle id="target-e-2" type="target" position={Position.Left} className="node-handle-direct visible source-handle"/>
      <Handle id="source-e-2" type="source" position={Position.Right} className="node-handle-direct visible source-handle"/>
      {/* { showFourHandles?
        : <>
          <Handle id="a" type="target" position={Position.Top} className="node-handle-direct target-handle"/>
          <Handle id="b" type="source" position={Position.Bottom} className="node-handle-direct source-handle"/>
        </>
      } */}
    </div>
  )
}

export default TopicNode;